/* =========================================================
   OH PANCHO! - Theme JS
   Plain JS, no build step, no dependencies.

   Two rules keep this working inside the Shopify theme editor:
     1. Anything global (cart, drawers, keyboard) is bound ONCE on
        document via delegation, so re-rendered sections keep working.
     2. Anything scoped to a section is guarded by a dataset flag and
        re-runs on `shopify:section:load`.
========================================================= */
(function () {
  "use strict";

  var settings = window.OPSettings || {};
  var routes = settings.routes || {};
  var strings = settings.strings || {};
  var fine = window.matchMedia && window.matchMedia("(pointer: fine)").matches;
  var reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* The `{% form 'product' %}` tag is what carries data-add-to-cart-form. The
     action fallback means the AJAX cart still works even if that attribute
     ever fails to render. */
  var ATC_FORM_SELECTOR = '[data-add-to-cart-form], form[action*="/cart/add"]';

  /* ---------- helpers ---------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function once(el, key) {
    if (!el || el.dataset[key] === "1") return false;
    el.dataset[key] = "1";
    return true;
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  /**
   * Formats cents using the store's own money_format, so currency and
   * separators follow Shopify's settings instead of being hardcoded.
   */
  function formatMoney(cents) {
    var format = settings.moneyFormat || "{{amount}}";
    var value = typeof cents === "string" ? parseInt(cents.replace(/[^0-9-]/g, ""), 10) : cents;
    if (isNaN(value)) value = 0;

    function withCommas(number, precision, thousands, decimal) {
      var n = (value / 100.0).toFixed(precision);
      var parts = n.split(".");
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      return parts[1] ? intPart + decimal + parts[1] : intPart;
    }
    /* Indian grouping: 1,00,000 rather than 100,000 */
    function withIndianCommas(precision, decimal) {
      var n = Math.abs(value / 100.0).toFixed(precision);
      var parts = n.split(".");
      var intPart = parts[0];
      var last3 = intPart.slice(-3);
      var rest = intPart.slice(0, -3);
      if (rest) last3 = rest.replace(/(\d)(?=(\d\d)+$)/g, "$1,") + "," + last3;
      var out = (value < 0 ? "-" : "") + last3;
      return parts[1] ? out + decimal + parts[1] : out;
    }

    var isIndian = (settings.moneyFormat || "").indexOf("Rs.") > -1 ||
                   (settings.moneyFormat || "").indexOf("₹") > -1;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case "amount": return isIndian ? withIndianCommas(2, ".") : withCommas(value, 2, ",", ".");
        case "amount_no_decimals": return isIndian ? withIndianCommas(0, ".") : withCommas(value, 0, ",", ".");
        case "amount_with_comma_separator": return withCommas(value, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return withCommas(value, 0, ".", ",");
        case "amount_with_apostrophe_separator": return withCommas(value, 2, "'", ".");
        default: return "";
      }
    });
  }

  /* ---------- toast ---------- */
  var toastTimer;
  function toast(message, isError) {
    var el = qs("[data-toast]");
    if (!el) return;
    el.textContent = message;
    el.classList.toggle("op-toast--error", !!isError);
    el.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove("is-visible"); }, 3200);
  }

  /* =========================================================
     Overlay / drawer manager
     One stack, so Escape and the overlay always close the top-most
     thing and focus returns where it started.
  ========================================================= */
  var Layers = (function () {
    var stack = [];
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function trap(e) {
      var top = stack[stack.length - 1];
      if (!top || e.key !== "Tab") return;
      var items = qsa(FOCUSABLE, top.panel).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open(panel, opts) {
      opts = opts || {};
      if (!panel || stack.some(function (l) { return l.panel === panel; })) return;
      var layer = {
        panel: panel,
        overlay: opts.overlay || null,
        /* An explicit trigger wins: closeLayer both restores focus to it and
           resets its aria-expanded, and those must be the same element. */
        trigger: opts.trigger || document.activeElement,
        onClose: opts.onClose
      };
      stack.push(layer);
      panel.classList.add("is-open");
      panel.setAttribute("aria-hidden", "false");
      if (layer.overlay) layer.overlay.classList.add("is-visible");
      document.body.classList.add("scroll-lock");
      if (layer.trigger && layer.trigger.hasAttribute && layer.trigger.hasAttribute("aria-expanded")) {
        layer.trigger.setAttribute("aria-expanded", "true");
      }
      setTimeout(function () {
        var focusTarget = panel.querySelector("[data-autofocus]") || panel.querySelector(FOCUSABLE);
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }, 120);
    }

    function closeLayer(layer) {
      layer.panel.classList.remove("is-open");
      layer.panel.setAttribute("aria-hidden", "true");
      if (layer.overlay) layer.overlay.classList.remove("is-visible");
      if (layer.trigger && document.contains(layer.trigger)) {
        layer.trigger.focus({ preventScroll: true });
        if (layer.trigger.hasAttribute("aria-expanded")) layer.trigger.setAttribute("aria-expanded", "false");
      }
      if (typeof layer.onClose === "function") layer.onClose();
    }

    function close(panel) {
      var idx = panel ? stack.findIndex(function (l) { return l.panel === panel; }) : stack.length - 1;
      if (idx < 0) return;
      closeLayer(stack[idx]);
      stack.splice(idx, 1);
      if (!stack.length) document.body.classList.remove("scroll-lock");
    }

    function closeAll() {
      while (stack.length) closeLayer(stack.pop());
      document.body.classList.remove("scroll-lock");
    }

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && stack.length) { e.preventDefault(); close(); }
      trap(e);
    });

    document.addEventListener("click", function (e) {
      if (e.target.classList && e.target.classList.contains("op-overlay") && stack.length) close();
    });

    return { open: open, close: close, closeAll: closeAll, isOpen: function (p) { return stack.some(function (l) { return l.panel === p; }); } };
  })();

  /* =========================================================
     Cart
     State lives on the server. Every mutation asks Shopify to
     re-render the cart-drawer section, so prices, discounts and
     currency formatting always come from Liquid.
  ========================================================= */
  var Cart = (function () {
    var SECTION_ID = "cart-drawer";
    var inflight = null;

    function drawer() { return qs("[data-cart-drawer]"); }
    function overlay() { return qs("[data-cart-overlay]"); }

    function open() {
      var d = drawer();
      if (!d) { window.location.href = routes.cart; return; }
      Layers.open(d, { overlay: overlay() });
    }
    function close() { var d = drawer(); if (d) Layers.close(d); }

    function setBusy(state) {
      var d = drawer();
      if (d) d.classList.toggle("is-busy", state);
    }

    function updateCount(count) {
      qsa("[data-cart-count]").forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
      qsa("[data-cart-count-text]").forEach(function (el) { el.textContent = count; });
    }

    function applySections(sections) {
      if (!sections) return;
      var html = sections[SECTION_ID];
      if (!html) return;
      var incoming = new DOMParser().parseFromString(html, "text/html");
      var fresh = incoming.querySelector("[data-cart-drawer-content]");
      var current = qs("[data-cart-drawer-content]");
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        initSectionScripts(current);
      }
    }

    function request(url, body) {
      body.sections = SECTION_ID;
      body.sections_url = window.location.pathname;
      setBusy(true);
      inflight = fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          return r.json().then(
            function (data) {
              if (!r.ok) throw data;
              return data;
            },
            function () {
              /* Non-JSON body — a proxy error page, a captive portal, an
                 outage. Never show the shopper a JSON parser message. */
              throw new Error(strings.cartError);
            }
          );
        })
        .then(function (data) {
          applySections(data.sections);
          /* /cart/add.js returns the line item, not the cart, so ask for the count */
          if (typeof data.item_count === "number") updateCount(data.item_count);
          else return fetch(routes.cart + ".js", { headers: { Accept: "application/json" } })
            .then(function (r) { return r.json(); })
            .then(function (cart) { updateCount(cart.item_count); return data; });
          return data;
        })
        .finally(function () { setBusy(false); });
      return inflight;
    }

    function add(id, quantity, sourceBtn) {
      return request(routes.cartAdd, { items: [{ id: id, quantity: quantity || 1 }] })
        .then(function (data) {
          if (sourceBtn) burstConfetti(sourceBtn);
          open();
          return data;
        })
        .catch(function (err) {
          toast((err && (err.description || err.message)) || strings.cartError, true);
          throw err;
        });
    }

    function change(key, quantity) {
      return request(routes.cartChange, { id: key, quantity: quantity })
        .catch(function (err) {
          toast((err && (err.description || err.message)) || strings.cartError, true);
        });
    }

    /* ---- delegated events: bound once, survive every re-render ---- */
    function bind() {
      document.addEventListener("click", function (e) {
        var openBtn = e.target.closest("[data-cart-open]");
        if (openBtn) { e.preventDefault(); open(); return; }

        var closeBtn = e.target.closest("[data-cart-close]");
        if (closeBtn) { e.preventDefault(); close(); return; }

        var quickAdd = e.target.closest("[data-quick-add]");
        if (quickAdd) {
          e.preventDefault();
          if (quickAdd.classList.contains("is-loading")) return;
          quickAdd.classList.add("is-loading");
          add(quickAdd.getAttribute("data-quick-add"), 1, quickAdd)
            .catch(function () {})
            .finally(function () { quickAdd.classList.remove("is-loading"); });
          return;
        }

        var line = e.target.closest("[data-cart-line]");
        if (!line) return;
        var key = line.getAttribute("data-cart-line");
        var input = qs("[data-qty-input]", line);
        var current = input ? parseInt(input.value, 10) || 0 : 0;

        if (e.target.closest("[data-qty-increase]")) { e.preventDefault(); change(key, current + 1); }
        else if (e.target.closest("[data-qty-decrease]")) { e.preventDefault(); change(key, Math.max(0, current - 1)); }
        else if (e.target.closest("[data-qty-remove]")) { e.preventDefault(); change(key, 0); }
      });

      document.addEventListener("change", function (e) {
        if (!e.target.matches("[data-qty-input]")) return;
        var line = e.target.closest("[data-cart-line]");
        if (!line) return;
        change(line.getAttribute("data-cart-line"), Math.max(0, parseInt(e.target.value, 10) || 0));
      });

      /* Add-to-cart forms, including ones injected by quick view */
      document.addEventListener("submit", function (e) {
        var form = e.target.closest(ATC_FORM_SELECTOR);
        if (!form) return;
        e.preventDefault();
        var btn = qs("[data-add-to-cart-btn]", form) || qs('[type="submit"]', form);
        var idInput = qs('[name="id"]', form);
        var qtyInput = qs('[name="quantity"]', form);
        if (!idInput || !idInput.value) return;
        if (btn) btn.classList.add("is-loading");
        add(idInput.value, qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1, btn)
          .catch(function () {})
          .finally(function () { if (btn) btn.classList.remove("is-loading"); });
      });
    }

    return { bind: bind, open: open, close: close, add: add, change: change };
  })();

  /* =========================================================
     Confetti
  ========================================================= */
  function burstConfetti(originEl) {
    if (reducedMotion || !originEl || !originEl.getBoundingClientRect) return;
    var rect = originEl.getBoundingClientRect();
    if (!rect.width) return;
    var glyphs = ["★", "✦", "●"];
    var colors = ["#ff3d9a", "#1f3ab0", "#ffd400", "#e8352b"];
    for (var i = 0; i < 10; i++) {
      var piece = document.createElement("span");
      piece.className = "op-confetti-piece";
      piece.textContent = glyphs[i % glyphs.length];
      piece.style.color = colors[i % colors.length];
      piece.style.left = (rect.left + rect.width / 2) + "px";
      piece.style.top = rect.top + "px";
      var angle = Math.random() * Math.PI * 2;
      var dist = 60 + Math.random() * 70;
      piece.style.setProperty("--dx", (Math.cos(angle) * dist).toFixed(0) + "px");
      piece.style.setProperty("--dy", (Math.sin(angle) * dist - 40).toFixed(0) + "px");
      piece.style.setProperty("--rot", (Math.random() * 360).toFixed(0) + "deg");
      document.body.appendChild(piece);
      setTimeout(function (p) { p.remove(); }, 950, piece);
    }
  }

  /* =========================================================
     Header: measure height, hide on scroll down
  ========================================================= */
  function initHeader() {
    var header = qs("[data-header]");
    if (!header) return;
    /* Shopify wraps each section in its own div; that wrapper is the direct
       child of <body>, so it is the element that can actually stick. */
    var group = header.closest(".shopify-section") || header;

    function measure() {
      document.documentElement.style.setProperty("--header-height", header.offsetHeight + "px");
    }
    measure();
    if (window.ResizeObserver) new ResizeObserver(measure).observe(header);

    if (!once(group, "opHeaderScroll")) return;
    if (header.getAttribute("data-hide-on-scroll") !== "true") return;

    var lastY = window.scrollY;
    var ticking = false;
    function update() {
      var y = window.scrollY;
      /* never hide while a drawer is open, or the close button vanishes */
      if (!document.body.classList.contains("scroll-lock")) {
        group.classList.toggle("is-hidden", y > lastY && y > 200);
      }
      lastY = y;
      ticking = false;
    }
    on(window, "scroll", function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* =========================================================
     Mobile nav
  ========================================================= */
  function initMobileNav(ctx) {
    qsa("[data-mobile-nav]", ctx).forEach(function (nav) {
      if (!once(nav, "opInit")) return;
      var overlay = qs("[data-mobile-nav-overlay]");
      document.addEventListener("click", function (e) {
        if (e.target.closest("[data-mobile-nav-open]")) {
          e.preventDefault();
          Layers.open(nav, { overlay: overlay, trigger: e.target.closest("[data-mobile-nav-open]") });
        } else if (e.target.closest("[data-mobile-nav-close]")) {
          e.preventDefault();
          Layers.close(nav);
        }
      });
    });
  }

  /* =========================================================
     Scroll reveal
  ========================================================= */
  function initReveal(ctx) {
    var els = qsa(".reveal, .reveal-scale, .reveal-stagger", ctx);
    if (!els.length) return;
    if (reducedMotion || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -50px 0px" });

    els.forEach(function (el) {
      if (el.classList.contains("reveal-stagger")) {
        qsa(":scope > *", el).forEach(function (child, i) { child.style.setProperty("--i", i); });
      }
      io.observe(el);
    });
  }

  /* =========================================================
     Parallax hero
  ========================================================= */
  function initParallaxHero(ctx) {
    if (reducedMotion) return;
    qsa("[data-parallax-hero]", ctx).forEach(function (hero) {
      if (!once(hero, "opInit")) return;
      var layers = qsa("[data-parallax-layer]", hero);
      if (!layers.length) return;
      var mouseX = 0, mouseY = 0, scrollOffset = 0, ticking = false;

      function apply() {
        layers.forEach(function (layer) {
          var depth = parseFloat(layer.getAttribute("data-parallax-layer")) || 10;
          var isBg = layer.classList.contains("hero__layer");
          var x = mouseX * depth;
          var y = mouseY * depth + (isBg ? scrollOffset : 0);
          layer.style.transform = "translate3d(" + x.toFixed(1) + "px," + y.toFixed(1) + "px,0)";
        });
        ticking = false;
      }

      if (fine) {
        on(hero, "mousemove", function (e) {
          var rect = hero.getBoundingClientRect();
          mouseX = (e.clientX - rect.left) / rect.width - 0.5;
          mouseY = (e.clientY - rect.top) / rect.height - 0.5;
          if (!ticking) { requestAnimationFrame(apply); ticking = true; }
        });
        on(hero, "mouseleave", function () { mouseX = 0; mouseY = 0; apply(); });
      }

      on(window, "scroll", function () {
        var rect = hero.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        scrollOffset = -rect.top * 0.12;
        if (!ticking) { requestAnimationFrame(apply); ticking = true; }
      }, { passive: true });
    });
  }

  /* =========================================================
     Tilt cards + magnetic buttons (desktop pointers only)
  ========================================================= */
  function initTilt(ctx) {
    if (!fine || reducedMotion) return;
    qsa("[data-tilt]", ctx).forEach(function (card) {
      if (!once(card, "opInit")) return;
      var inner = qs(".tilt-card__inner", card) || card;
      var max = 7;
      on(card, "mousemove", function (e) {
        var rect = card.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        card.classList.add("is-tilting");
        inner.style.setProperty("--tilt-x", ((0.5 - py) * max * 2).toFixed(2) + "deg");
        inner.style.setProperty("--tilt-y", ((px - 0.5) * max * 2).toFixed(2) + "deg");
      });
      on(card, "mouseleave", function () {
        card.classList.remove("is-tilting");
        inner.style.setProperty("--tilt-x", "0deg");
        inner.style.setProperty("--tilt-y", "0deg");
      });
    });
  }

  function initMagnetic(ctx) {
    if (!fine || reducedMotion) return;
    qsa("[data-magnetic]", ctx).forEach(function (btn) {
      if (!once(btn, "opInit")) return;
      on(btn, "mousemove", function (e) {
        var r = btn.getBoundingClientRect();
        btn.style.transform = "translate(" +
          ((e.clientX - r.left - r.width / 2) * 0.16).toFixed(1) + "px," +
          ((e.clientY - r.top - r.height / 2) * 0.26).toFixed(1) + "px)";
      });
      on(btn, "mouseleave", function () { btn.style.transform = ""; });
    });
  }

  /* =========================================================
     Accordions
  ========================================================= */
  function initAccordions(ctx) {
    qsa("[data-accordion-trigger]", ctx).forEach(function (trigger) {
      if (!once(trigger, "opInit")) return;
      var item = trigger.closest(".accordion-item");
      var panel = qs(".accordion-item__panel", item);
      if (!panel) return;
      if (!panel.id) panel.id = "acc-" + Math.random().toString(36).slice(2, 9);
      trigger.setAttribute("aria-controls", panel.id);
      trigger.setAttribute("aria-expanded", item.classList.contains("is-open") ? "true" : "false");
      if (item.classList.contains("is-open")) panel.style.maxHeight = panel.scrollHeight + "px";

      on(trigger, "click", function () {
        var willOpen = !item.classList.contains("is-open");
        item.classList.toggle("is-open", willOpen);
        trigger.setAttribute("aria-expanded", willOpen ? "true" : "false");
        panel.style.maxHeight = willOpen ? panel.scrollHeight + "px" : null;
      });
    });
  }

  /* =========================================================
     Countdown
  ========================================================= */
  function initCountdowns(ctx) {
    qsa("[data-countdown-target]", ctx).forEach(function (box) {
      if (!once(box, "opInit")) return;
      var target = new Date(box.getAttribute("data-countdown-target")).getTime();
      var wrapper = box.closest("[data-countdown-wrapper]") || box;
      if (isNaN(target)) { wrapper.hidden = true; return; }

      var units = {
        days: qs("[data-cd-days]", box), hours: qs("[data-cd-hours]", box),
        mins: qs("[data-cd-mins]", box), secs: qs("[data-cd-secs]", box)
      };
      function pad(n) { return String(n).padStart(2, "0"); }
      var timer = setInterval(tick, 1000);

      function tick() {
        var diff = target - Date.now();
        if (diff <= 0) {
          clearInterval(timer);
          if (box.getAttribute("data-countdown-hide-on-end") === "true") wrapper.hidden = true;
          else Object.keys(units).forEach(function (k) { if (units[k]) units[k].textContent = "00"; });
          return;
        }
        if (units.days) units.days.textContent = pad(Math.floor(diff / 86400000));
        if (units.hours) units.hours.textContent = pad(Math.floor((diff % 86400000) / 3600000));
        if (units.mins) units.mins.textContent = pad(Math.floor((diff % 3600000) / 60000));
        if (units.secs) units.secs.textContent = pad(Math.floor((diff % 60000) / 1000));
      }
      tick();
    });
  }

  /* =========================================================
     Size guide drawer
  ========================================================= */
  function initSizeGuide(ctx) {
    qsa("[data-size-guide]", ctx).forEach(function (root) {
      if (!once(root, "opInit")) return;
      var drawer = qs("[data-sg-drawer]", root);
      var overlay = qs("[data-sg-overlay]", root);
      var openBtn = qs("[data-sg-open]", root);

      on(openBtn, "click", function () { Layers.open(drawer, { overlay: overlay, trigger: openBtn }); });
      on(qs("[data-sg-close]", root), "click", function () { Layers.close(drawer); });

      var tabs = qsa("[data-sg-tab]", root);
      var panels = qsa("[data-sg-panel]", root);
      tabs.forEach(function (tab) {
        on(tab, "click", function () {
          var target = tab.getAttribute("data-sg-tab");
          tabs.forEach(function (t) {
            var active = t === tab;
            t.classList.toggle("is-active", active);
            t.setAttribute("aria-selected", active ? "true" : "false");
          });
          panels.forEach(function (p) { p.hidden = p.getAttribute("data-sg-panel") !== target; });
        });
      });
    });
  }

  /* =========================================================
     Product form: variant picker, gallery, quantity
  ========================================================= */
  function initProductForm(ctx) {
    qsa("[data-product-form-root]", ctx).forEach(function (section) {
      if (!once(section, "opInit")) return;

      var dataEl = qs("[data-variants-json]", section);
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var form = qs(ATC_FORM_SELECTOR, section);
      var idInput = form ? qs('input[name="id"]', form) : null;
      var priceEl = qs("[data-product-price]", section);
      var compareEl = qs("[data-product-compare-price]", section);
      var discountEl = qs("[data-product-discount]", section);
      var addBtn = qs("[data-add-to-cart-btn]", section);
      var inventoryEl = qs("[data-variant-inventory]", section);
      var skuEl = qs("[data-variant-sku]", section);
      var groups = qsa("[data-option-position]", section);
      var selected = {};

      groups.forEach(function (group) {
        var pos = group.getAttribute("data-option-position");
        var active = qs("[data-option-value].is-selected", group);
        if (active) selected[pos] = active.getAttribute("data-option-value");
      });

      function matches(map) {
        return variants.find(function (v) {
          return v.options.every(function (val, idx) { return map[String(idx + 1)] === val; });
        });
      }

      /**
       * Marks option values that lead nowhere.
       *
       * Only the options BEFORE this one constrain the answer. Comparing
       * against the full selection would strike out every colour the moment a
       * sold-out size is picked, which reads as "this product is gone" instead
       * of "that size is gone".
       *
       * Values stay clickable either way — shoppers need to see which size
       * sold out, not have it silently disappear.
       */
      function markUnavailable() {
        groups.forEach(function (group) {
          var pos = parseInt(group.getAttribute("data-option-position"), 10);
          qsa("[data-option-value]", group).forEach(function (btn) {
            var value = btn.getAttribute("data-option-value");
            var reachable = variants.some(function (v) {
              if (!v.available || v.options[pos - 1] !== value) return false;
              for (var i = 1; i < pos; i++) {
                if (selected[String(i)] && v.options[i - 1] !== selected[String(i)]) return false;
              }
              return true;
            });
            btn.classList.toggle("is-unavailable", !reachable);
          });
        });
      }

      function render(userInitiated) {
        var variant = matches(selected);
        markUnavailable();

        if (!variant) {
          if (addBtn) {
            addBtn.disabled = true;
            var l = qs(".btn__label", addBtn);
            if (l) l.textContent = strings.unavailable;
          }
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) {
          var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compareEl.textContent = onSale ? formatMoney(variant.compare_at_price) : "";
          compareEl.hidden = !onSale;
          var wrap = compareEl.closest(".price");
          if (wrap) wrap.classList.toggle("price--on-sale", !!onSale);
          if (discountEl) {
            if (onSale) {
              discountEl.hidden = false;
              discountEl.textContent = "-" + Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) + "%";
            } else {
              discountEl.hidden = true;
            }
          }
        }
        if (skuEl) skuEl.textContent = variant.sku || "";
        if (inventoryEl) {
          var showLow = variant.available && variant.inventory_management === "shopify" &&
                        variant.inventory_quantity > 0 &&
                        variant.inventory_quantity <= parseInt(inventoryEl.getAttribute("data-low-threshold") || "10", 10);
          inventoryEl.hidden = !showLow;
          if (showLow) {
            var out = qs("[data-inventory-text]", inventoryEl);
            if (out) out.textContent = out.getAttribute("data-template").replace("__COUNT__", variant.inventory_quantity);
          }
        }
        if (addBtn) {
          addBtn.disabled = !variant.available;
          var label = qs(".btn__label", addBtn);
          if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
        }

        qsa("[data-option-selected-for]", section).forEach(function (el) {
          var pos = el.getAttribute("data-option-selected-for");
          el.textContent = selected[pos] || "";
        });

        if (userInitiated && variant.featured_media) {
          goToMedia(section, variant.featured_media.id);
          /* Keep the URL shareable and the back button honest */
          if (window.history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set("variant", variant.id);
            window.history.replaceState({}, "", url.toString());
          }
        }

        document.dispatchEvent(new CustomEvent("op:variant:change", {
          detail: { variant: variant, section: section }
        }));
      }

      groups.forEach(function (group) {
        var pos = group.getAttribute("data-option-position");
        qsa("[data-option-value]", group).forEach(function (btn) {
          on(btn, "click", function () {
            qsa("[data-option-value]", group).forEach(function (b) {
              b.classList.remove("is-selected");
              b.setAttribute("aria-pressed", "false");
            });
            btn.classList.add("is-selected");
            btn.setAttribute("aria-pressed", "true");
            selected[pos] = btn.getAttribute("data-option-value");
            render(true);
          });
        });
      });

      render(false);

      var qtyWrap = qs("[data-qty-stepper]", section);
      if (qtyWrap) {
        var qtyInput = qs("input", qtyWrap);
        on(qs("[data-qty-plus]", qtyWrap), "click", function () { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; });
        on(qs("[data-qty-minus]", qtyWrap), "click", function () { qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1); });
      }
    });
  }

  /* =========================================================
     Product gallery: swipe on mobile, thumbs + zoom on desktop
  ========================================================= */
  function goToMedia(section, mediaId) {
    var slide = qs('[data-media-id="' + mediaId + '"]', section);
    var track = qs("[data-gallery-slides]", section);
    if (slide && track) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: "smooth" });
  }

  function initGallery(ctx) {
    qsa("[data-gallery]", ctx).forEach(function (gallery) {
      if (!once(gallery, "opInit")) return;
      var track = qs("[data-gallery-slides]", gallery);
      var slides = qsa("[data-media-id]", track);
      var thumbs = qsa("[data-gallery-thumb]", gallery);
      var dots = qsa("[data-gallery-dot]", gallery);
      if (!track || !slides.length) return;

      function setActive(index) {
        thumbs.forEach(function (t, i) { t.classList.toggle("is-active", i === index); });
        dots.forEach(function (d, i) { d.classList.toggle("is-active", i === index); });
      }

      thumbs.forEach(function (thumb, i) {
        on(thumb, "click", function () {
          track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: "smooth" });
          setActive(i);
        });
      });

      on(track, "scroll", debounce(function () {
        var index = Math.round(track.scrollLeft / track.clientWidth);
        setActive(Math.min(index, slides.length - 1));
      }, 80), { passive: true });

      /* Click-to-zoom, desktop pointers only */
      if (fine) {
        slides.forEach(function (slide) {
          slide.classList.add("is-zoomable");
          var img = qs("img", slide);
          if (!img) return;
          on(slide, "click", function () { slide.classList.toggle("is-zoomed"); });
          on(slide, "mousemove", function (e) {
            if (!slide.classList.contains("is-zoomed")) return;
            var r = slide.getBoundingClientRect();
            img.style.transformOrigin =
              (((e.clientX - r.left) / r.width) * 100).toFixed(1) + "% " +
              (((e.clientY - r.top) / r.height) * 100).toFixed(1) + "%";
          });
          on(slide, "mouseleave", function () { slide.classList.remove("is-zoomed"); });
        });
      }

      setActive(0);
    });
  }

  /* =========================================================
     Sticky add-to-cart bar
  ========================================================= */
  function initStickyAtc(ctx) {
    qsa("[data-sticky-atc]", ctx).forEach(function (bar) {
      if (!once(bar, "opInit")) return;
      var form = qs(ATC_FORM_SELECTOR);
      var realBtn = form ? qs("[data-add-to-cart-btn]", form) : null;
      var stickyBtn = qs("[data-sticky-atc-btn]", bar);
      var picker = qs("[data-option-position]");
      if (!realBtn) { bar.remove(); return; }

      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            bar.classList.toggle("is-visible", !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        }, { threshold: 0 }).observe(realBtn);
      }

      on(stickyBtn, "click", function () {
        /* Shopify preselects a variant, so the normal case is a straight add.
           When the chosen combination is sold out the bar sends the shopper
           back to the picker instead — see the label logic below. */
        if (realBtn.disabled && picker) {
          picker.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        realBtn.click();
      });

      document.addEventListener("op:variant:change", function (e) {
        var v = e.detail.variant;
        var priceEl = qs("[data-sticky-atc-price]", bar);
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (!stickyBtn) return;

        var label = qs(".btn__label", stickyBtn) || stickyBtn;
        if (v.available) {
          stickyBtn.disabled = false;
          label.textContent = strings.addToCart;
        } else if (picker) {
          /* Deliberately NOT disabled: a disabled button swallows the click,
             which would strand a mobile shopper who has scrolled past the
             size picker. Tapping it takes them back to choose another size. */
          stickyBtn.disabled = false;
          label.textContent = strings.chooseAnother;
        } else {
          stickyBtn.disabled = true;
          label.textContent = strings.soldOut;
        }
      });
    });
  }

  /* =========================================================
     Section-rendered blocks fetched over the network
     (recommendations, recently viewed, complete the look)
  ========================================================= */
  function initFetchSections(ctx) {
    qsa("[data-fetch-section]", ctx).forEach(function (el) {
      if (!once(el, "opInit")) return;
      var url = el.getAttribute("data-fetch-section");
      if (!url) return;

      function load() {
        fetch(url)
          .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(r.status)); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var content = doc.querySelector("[data-fetch-content]");
            if (content && content.innerHTML.trim()) {
              el.innerHTML = content.innerHTML;
              el.hidden = false;
              initSectionScripts(el);
            } else {
              el.hidden = true;
            }
          })
          .catch(function () { el.hidden = true; });
      }

      /* These containers start hidden so they never leave a gap when Shopify
         has nothing to recommend — which also means IntersectionObserver
         would never fire on them. Load once the page is idle instead: it
         stays off the critical path without depending on visibility. */
      if (document.readyState === "complete") setTimeout(load, 200);
      else on(window, "load", function () { setTimeout(load, 200); });
    });
  }

  /* =========================================================
     Recently viewed
  ========================================================= */
  var RecentlyViewed = (function () {
    var KEY = "op:recently-viewed";
    var MAX = 12;

    function read() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    }
    function record(handle) {
      if (!handle) return;
      try {
        var list = read().filter(function (h) { return h !== handle; });
        list.unshift(handle);
        localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      } catch (e) { /* private mode: silently skip */ }
    }
    function init(ctx) {
      var marker = qs("[data-record-recently-viewed]", ctx);
      if (marker) record(marker.getAttribute("data-record-recently-viewed"));

      qsa("[data-recently-viewed]", ctx).forEach(function (el) {
        if (!once(el, "opInit")) return;
        var exclude = el.getAttribute("data-exclude");
        var limit = parseInt(el.getAttribute("data-limit") || "4", 10);
        var handles = read().filter(function (h) { return h !== exclude; }).slice(0, limit);
        if (!handles.length) { el.hidden = true; return; }

        /* Shopify's search endpoint accepts handle: terms, so one request
           fetches the whole list already rendered as product cards. */
        var query = handles.map(function (h) { return "handle:" + h; }).join(" OR ");
        var url = routes.search + "?type=product&options[unavailable_products]=last" +
                  "&section_id=recently-viewed&q=" + encodeURIComponent(query);

        fetch(url)
          .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(r.status)); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var content = doc.querySelector("[data-fetch-content]");
            if (content && content.querySelector("[data-product-card]")) {
              el.innerHTML = content.innerHTML;
              el.hidden = false;
              initSectionScripts(el);
            } else {
              el.hidden = true;
            }
          })
          .catch(function () { el.hidden = true; });
      });
    }
    return { init: init, record: record };
  })();

  /* =========================================================
     Predictive search
  ========================================================= */
  function initSearch() {
    var drawer = qs("[data-search-drawer]");
    if (!drawer || !once(drawer, "opInit")) return;
    var overlay = qs("[data-search-overlay]");
    var input = qs("[data-search-input]", drawer);
    var results = qs("[data-predictive-results]", drawer);
    var defaults = qs("[data-search-default]", drawer);
    var controller = null;

    document.addEventListener("click", function (e) {
      var trigger = e.target.closest("[data-search-open]");
      if (trigger) { e.preventDefault(); Layers.open(drawer, { overlay: overlay, trigger: trigger }); }
      else if (e.target.closest("[data-search-close]")) { e.preventDefault(); Layers.close(drawer); }
    });

    if (!input || !results) return;

    var search = debounce(function () {
      var term = input.value.trim();
      if (controller) controller.abort();

      if (term.length < 2) {
        results.innerHTML = "";
        if (defaults) defaults.hidden = false;
        return;
      }
      if (defaults) defaults.hidden = true;

      controller = new AbortController();
      var url = routes.predictiveSearch +
        "?q=" + encodeURIComponent(term) +
        "&resources[type]=product,query,collection" +
        "&resources[limit]=6" +
        "&section_id=predictive-search";

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(r.status)); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var fresh = doc.querySelector("[data-predictive-results]");
          results.innerHTML = fresh ? fresh.innerHTML : "";
        })
        .catch(function (err) { if (err.name !== "AbortError") results.innerHTML = ""; });
    }, 250);

    on(input, "input", search);
  }

  /* =========================================================
     Quick view
  ========================================================= */
  function initQuickView() {
    var modal = qs("[data-quick-view]");
    if (!modal || !once(modal, "opInit")) return;
    var panel = qs("[data-quick-view-panel]", modal);
    var overlay = qs("[data-quick-view-overlay]");

    document.addEventListener("click", function (e) {
      var trigger = e.target.closest("[data-quick-view-open]");
      if (trigger) {
        e.preventDefault();
        var handle = trigger.getAttribute("data-quick-view-open");
        panel.innerHTML = '<div class="quick-view__loading"><span class="btn__spinner" style="display:inline-block;width:28px;height:28px;color:var(--op-black);"></span></div>';
        Layers.open(modal, { overlay: overlay, trigger: trigger });

        fetch("/products/" + handle + "?section_id=quick-view")
          .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(r.status)); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var content = doc.querySelector("[data-fetch-content]");
            panel.innerHTML = content ? content.innerHTML : "";
            initSectionScripts(panel);
          })
          .catch(function () {
            Layers.close(modal);
            window.location.href = "/products/" + handle;
          });
        return;
      }
      if (e.target.closest("[data-quick-view-close]")) { e.preventDefault(); Layers.close(modal); }
    });
  }

  /* =========================================================
     Collection facets
  ========================================================= */
  function initFacets(ctx) {
    qsa("[data-facets]", ctx).forEach(function (root) {
      if (!once(root, "opInit")) return;
      var form = qs("[data-facets-form]", root);

      qsa("[data-facet-toggle]", root).forEach(function (toggle) {
        on(toggle, "click", function () {
          var panel = qs("#" + toggle.getAttribute("aria-controls"), root);
          var isOpen = toggle.getAttribute("aria-expanded") === "true";
          qsa("[data-facet-toggle]", root).forEach(function (t) {
            t.setAttribute("aria-expanded", "false");
            var p = qs("#" + t.getAttribute("aria-controls"), root);
            if (p) p.hidden = true;
          });
          if (!isOpen && panel) { toggle.setAttribute("aria-expanded", "true"); panel.hidden = false; }
        });
      });

      document.addEventListener("click", function (e) {
        if (!root.contains(e.target)) {
          qsa("[data-facet-toggle]", root).forEach(function (t) {
            t.setAttribute("aria-expanded", "false");
            var p = qs("#" + t.getAttribute("aria-controls"), root);
            if (p) p.hidden = true;
          });
        }
      });

      /* Auto-submit so filtering feels instant without a custom fetch layer */
      if (form) {
        on(form, "change", function () { form.submit(); });
      }
    });

    qsa("[data-sort-select]", ctx).forEach(function (select) {
      if (!once(select, "opInit")) return;
      on(select, "change", function () {
        var url = new URL(window.location.href);
        url.searchParams.set("sort_by", select.value);
        url.searchParams.delete("page");
        window.location.href = url.toString();
      });
    });
  }

  /* =========================================================
     Newsletter popup
  ========================================================= */
  function initNewsletterPopup(ctx, forceEditorPreview) {
    var popup = qs("[data-newsletter-popup]", ctx);
    if (!popup) return;

    var KEY = "op:newsletter-dismissed";
    var overlay = qs("[data-newsletter-overlay]");
    var delay = parseInt(popup.getAttribute("data-delay") || "8", 10) * 1000;
    var days = parseInt(popup.getAttribute("data-remember-days") || "30", 10);
    var inEditor = window.Shopify && window.Shopify.designMode;

    /* Never cover the editor's page picker on every route. Merchants can
       still preview the popup by selecting its section in the sidebar. */
    if (inEditor && !forceEditorPreview) return;
    if (!once(popup, "opInit")) return;

    function dismissed() {
      if (inEditor) return false;
      try {
        var until = parseInt(localStorage.getItem(KEY) || "0", 10);
        return until > Date.now();
      } catch (e) { return true; }
    }
    function remember() {
      try { localStorage.setItem(KEY, String(Date.now() + days * 86400000)); } catch (e) {}
    }

    if (dismissed()) return;
    if (qs("[data-newsletter-posted]", popup)) { remember(); }

    var shown = false;
    function show() {
      if (shown || dismissed() || document.body.classList.contains("scroll-lock")) return;
      shown = true;
      Layers.open(popup, { overlay: overlay, onClose: remember });
    }

    if (forceEditorPreview) show();
    else setTimeout(show, delay);

    if (popup.getAttribute("data-exit-intent") === "true" && fine) {
      document.addEventListener("mouseout", function (e) {
        if (!e.relatedTarget && e.clientY <= 0) show();
      });
    }

    qsa("[data-newsletter-close]", popup).forEach(function (btn) {
      on(btn, "click", function () { remember(); Layers.close(popup); });
    });

    /* A submitted form means they joined: never nag again */
    on(qs("form", popup), "submit", remember);
  }

  /* =========================================================
     Share button
  ========================================================= */
  function initShare(ctx) {
    qsa("[data-share]", ctx).forEach(function (btn) {
      if (!once(btn, "opInit")) return;
      on(btn, "click", function () {
        var url = btn.getAttribute("data-share") || window.location.href;
        if (navigator.share) {
          navigator.share({ title: document.title, url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () { toast(strings.linkCopied); });
        }
      });
    });
  }

  /* =========================================================
     Boot
  ========================================================= */
  function initSectionScripts(ctx) {
    initReveal(ctx);
    initTilt(ctx);
    initMagnetic(ctx);
    initAccordions(ctx);
    initCountdowns(ctx);
    initSizeGuide(ctx);
    initProductForm(ctx);
    initGallery(ctx);
    initStickyAtc(ctx);
    initFetchSections(ctx);
    initFacets(ctx);
    initShare(ctx);
    initMobileNav(ctx);
    initParallaxHero(ctx);
  }

  function boot() {
    initHeader();
    initSectionScripts(document);
    initSearch();
    initQuickView();
    initNewsletterPopup(document);
    RecentlyViewed.init(document);
    Cart.bind();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Theme editor hooks */
  document.addEventListener("shopify:section:load", function (e) {
    initHeader();
    initSectionScripts(e.target);
    initSearch();
    initNewsletterPopup(e.target);
    RecentlyViewed.init(e.target);
  });
  document.addEventListener("shopify:section:unload", function () { Layers.closeAll(); });
  document.addEventListener("shopify:section:select", function (e) {
    initSectionScripts(e.target);
    initNewsletterPopup(e.target, true);
  });
  document.addEventListener("shopify:block:select", function (e) {
    var item = e.target.closest(".accordion-item");
    if (item) {
      var trigger = qs("[data-accordion-trigger]", item);
      if (trigger && !item.classList.contains("is-open")) trigger.click();
    }
  });

  /* Public surface, handy for apps and for debugging in the console */
  window.OP = {
    Cart: Cart,
    Layers: Layers,
    formatMoney: formatMoney,
    toast: toast,
    burstConfetti: burstConfetti,
    refresh: initSectionScripts
  };
})();
